import { useEffect, useState } from 'react';
import { posApi } from '../api/pos';
import { unwrapResponse } from '../api/client';
import { toast } from './toast';

type SessionType = 'counter' | 'outlet';

const PaymentMethodType = {
  Bank: 1,
  MobileBank: 2,
} as const;

interface PaymentRow {
  method: any;
  qty: any;
  amount: number;
  methodType: number | null;
}

interface PaymentSummaryModalProps {
  sessionId: number;
  type: SessionType;
  onClose: () => void;
}

function resolveMethodType(payment: any): number | null {
  const rawType =
    payment?.methodType ??
    payment?.MethodType ??
    payment?.methodTypeId ??
    payment?.MethodTypeId ??
    payment?.paymentMethodType ??
    payment?.PaymentMethodType ??
    payment?.paymentMethodTypeId ??
    payment?.PaymentMethodTypeId ??
    payment?.paymentMethodType?.id ??
    payment?.PaymentMethodType?.id ??
    payment?.paymentMethod?.paymentMethodTypeId ??
    payment?.PaymentMethod?.PaymentMethodTypeId;

  if (rawType === undefined || rawType === null) {
    return null;
  }

  if (typeof rawType === 'string') {
    const lower = rawType.toLowerCase();
    if (lower.includes('bank') && !lower.includes('mobile')) {
      return PaymentMethodType.Bank;
    }
    if (lower.includes('mobile')) {
      return PaymentMethodType.MobileBank;
    }
    const numeric = Number(rawType);
    return Number.isNaN(numeric) ? null : numeric;
  }

  return Number(rawType);
}

function normalizePayments(payments: any): PaymentRow[] {
  if (!Array.isArray(payments)) {
    return [];
  }

  return payments
    .map((payment) => ({
      method:
        payment?.method ??
        payment?.Method ??
        payment?.paymentMethod ??
        payment?.PaymentMethod,
      qty: payment?.qty ?? payment?.Qty ?? payment?.quantity ?? payment?.Quantity,
      amount: Number(
        payment?.amount ??
          payment?.Amount ??
          payment?.totalAmount ??
          payment?.TotalAmount ??
          0
      ),
      methodType: resolveMethodType(payment),
    }))
    .filter(
      (payment) =>
        payment.method !== undefined || payment.qty !== undefined || Number.isFinite(payment.amount)
    );
}

export default function PaymentSummaryModal({ sessionId, type, onClose }: PaymentSummaryModalProps) {
  const [loading, setLoading] = useState(true);
  const [bankPayments, setBankPayments] = useState<PaymentRow[]>([]);
  const [mobilePayments, setMobilePayments] = useState<PaymentRow[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!sessionId) {
        toast.error('Session id not found.');
        onClose();
        return;
      }
      setLoading(true);
      try {
        const res =
          type === 'counter'
            ? await posApi.getCounterSessionPaymentSummary(sessionId)
            : await posApi.getOutletSessionPaymentSummary(sessionId);
        const summary = unwrapResponse<any>(res);
        if (cancelled) return;
        const normalized = normalizePayments(summary?.paymentsInfo ?? summary?.PaymentsInfo);
        setBankPayments(normalized.filter((p) => p.methodType === PaymentMethodType.Bank));
        setMobilePayments(normalized.filter((p) => p.methodType === PaymentMethodType.MobileBank));
      } catch (err: any) {
        if (cancelled) return;
        toast.error(err?.message || 'Failed to load payment summary.');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, type]);

  const bankTotal = bankPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const mobileTotal = mobilePayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const hasPayments = bankPayments.length > 0 || mobilePayments.length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <h2 className="text-base font-semibold text-gray-900">Bank/Mobile Bank Payments</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <i className="ri-loader-4-line animate-spin text-2xl text-teal-600 mr-2"></i>
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          ) : !hasPayments ? (
            <p className="text-sm text-gray-500 text-center py-6">No payment records found.</p>
          ) : (
            <div className="space-y-4">
              {bankPayments.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Bank</p>
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-1/3" />
                      <col className="w-1/3" />
                      <col className="w-1/3" />
                    </colgroup>
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-1 font-medium whitespace-nowrap">Method</th>
                        <th className="py-1 font-medium text-center whitespace-nowrap">Qty</th>
                        <th className="py-1 font-medium text-right whitespace-nowrap">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bankPayments.map((payment, idx) => (
                        <tr key={idx} className="border-b border-gray-100">
                          <td className="py-1.5 text-gray-900 truncate">{payment.method || 'N/A'}</td>
                          <td className="py-1.5 text-center text-gray-900 whitespace-nowrap">{payment.qty || 0}</td>
                          <td className="py-1.5 text-right text-gray-900 whitespace-nowrap">৳{payment.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} className="pt-2 text-right font-semibold text-gray-900 whitespace-nowrap">
                          ৳{bankTotal.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {mobilePayments.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Mobile Bank</p>
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-1/3" />
                      <col className="w-1/3" />
                      <col className="w-1/3" />
                    </colgroup>
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-1 font-medium whitespace-nowrap">Method</th>
                        <th className="py-1 font-medium text-center whitespace-nowrap">Qty</th>
                        <th className="py-1 font-medium text-right whitespace-nowrap">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mobilePayments.map((payment, idx) => (
                        <tr key={idx} className="border-b border-gray-100">
                          <td className="py-1.5 text-gray-900 truncate">{payment.method || 'N/A'}</td>
                          <td className="py-1.5 text-center text-gray-900 whitespace-nowrap">{payment.qty || 0}</td>
                          <td className="py-1.5 text-right text-gray-900 whitespace-nowrap">৳{payment.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} className="pt-2 text-right font-semibold text-gray-900 whitespace-nowrap">
                          ৳{mobileTotal.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
